import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { Search, ChevronDown, Send, MessageCircle, CheckCircle2 } from 'lucide-react';

import {
  feedbackApi,
  FEEDBACK_CATEGORY_LABELS,
  type FeedbackCategory,
} from '@/lib/api/feedback';

interface FaqItem {
  q: string;
  a: string;
}

interface FaqCategory {
  label: string;
  items: FaqItem[];
}

const FAQ_CATEGORIES: FaqCategory[] = [
  {
    label: 'Getting Started',
    items: [
      { q: 'How do I join my batch?', a: 'After your college admin enrols you, log in with the credentials shared by your TPO. Your batch will appear on the Home and My Courses pages automatically.' },
      { q: 'I forgot my password. How do I reset it?', a: 'Click "Forgot password" on the login screen and enter your registered email. You will receive a reset link within a few minutes. Check your spam folder if it doesn\'t arrive.' },
      { q: 'Can I switch between multiple batches?', a: 'Yes. Use the batch switcher in the top navigation bar to toggle between any batch you are enrolled in.' },
      { q: 'How do I update my profile or avatar?', a: 'Go to Profile (click your name in the top-right dropdown) and edit your details there.' },
    ],
  },
  {
    label: 'Live Sessions',
    items: [
      { q: 'How do I join a live class?', a: 'When a session is within 15 minutes of starting, the "Join Session" button on your Home page becomes active. Click it to open the meeting link in a new tab.' },
      { q: 'Where can I watch a recording after the session?', a: 'Open the course topic from My Courses and switch to the "Recording" tab. Recordings are usually available within a few hours of the session ending.' },
      { q: 'The meeting link isn\'t working. What should I do?', a: 'Try refreshing the page. If the button still doesn\'t appear, contact your batch trainer or TPO — the meeting URL may need to be updated in the system.' },
      { q: 'Will I get a reminder before a live class?', a: 'Yes. Check the Today\'s Events section on your Home dashboard, which shows all sessions and deadlines for the day.' },
    ],
  },
  {
    label: 'Assignments & Assessments',
    items: [
      { q: 'How do I submit an assignment?', a: 'Navigate to the relevant course topic, open the "Practice" tab, and click the assignment. Submit before the due date shown on the card.' },
      { q: 'Can I resubmit an assignment?', a: 'Resubmission depends on your trainer\'s settings. If the option is available, you\'ll see a "Resubmit" button after your first submission.' },
      { q: 'Where do I see my grades?', a: 'Grades appear inside each assignment once your trainer marks them. You can also track overall progress on the Progress page.' },
      { q: 'What happens if I miss a due date?', a: 'The assignment will be marked overdue in your Due Items list. You may still be able to submit late — contact your trainer to confirm.' },
    ],
  },
  {
    label: 'Problems & Coding',
    items: [
      { q: 'My code runs locally but fails on the judge. Why?', a: 'Common reasons: different input format, edge cases not handled, or language-specific behaviour differences. Read the problem statement carefully and check the sample test cases.' },
      { q: 'Which programming languages are supported?', a: 'C++, Python, Java, and Node.js are currently supported in the online judge.' },
      { q: 'The judge is taking too long. Is it down?', a: 'The judge may be under heavy load. Wait a minute and resubmit. If the issue persists, contact support.' },
    ],
  },
  {
    label: 'Technical Issues',
    items: [
      { q: 'The platform is slow or not loading. What should I try?', a: 'Hard-refresh the page (Ctrl+Shift+R / Cmd+Shift+R), clear your browser cache, or try a different browser. Chrome and Firefox are recommended.' },
      { q: 'Video in the recording tab is not playing.', a: 'Make sure your browser allows autoplay for this site. If the video still doesn\'t load, try disabling browser extensions or opening in an incognito window.' },
      { q: 'I\'m seeing a blank screen after logging in.', a: 'This is usually caused by a stale session. Log out, clear cookies for this site, and log in again. If the problem persists, email us.' },
      { q: 'Is the platform mobile-friendly?', a: 'Yes — the student portal works on modern mobile browsers. For the best experience with coding problems we recommend a desktop.' },
    ],
  },
];

function Accordion({ items }: { items: FaqItem[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="divide-y divide-border">
      {items.map((item, i) => (
        <div key={i}>
          <button
            type="button"
            onClick={() => setOpen(open === i ? null : i)}
            className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/40"
          >
            <span className="text-sm font-medium text-foreground">{item.q}</span>
            <ChevronDown
              className={`mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open === i ? 'rotate-180' : ''}`}
            />
          </button>
          {open === i && (
            <div className="px-5 pb-4 text-sm leading-relaxed text-muted-foreground">
              {item.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const CATEGORY_OPTIONS = Object.entries(FEEDBACK_CATEGORY_LABELS) as [FeedbackCategory, string][];

function ContactForm() {
  const [category, setCategory] = useState<FeedbackCategory>('technical');
  const [message, setMessage] = useState('');

  const { mutate, isPending, isSuccess, isError, reset } = useMutation({
    mutationFn: () => feedbackApi.submit(category, message.trim()),
    onSuccess: () => {
      setMessage('');
      setTimeout(reset, 5000);
    },
  });

  if (isSuccess) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-700">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        Thanks — we&rsquo;ll get back to you within one business day.
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (message.trim().length > 0) mutate();
      }}
      className="space-y-3"
    >
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
        className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#e8660d]/20"
      >
        {CATEGORY_OPTIONS.map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={1000}
        rows={4}
        placeholder="Describe your issue or question…"
        className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#e8660d]/20"
      />
      {isError && (
        <p className="text-sm text-red-600">Couldn&rsquo;t send your message. Please try again.</p>
      )}
      <button
        type="submit"
        disabled={isPending || message.trim().length === 0}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#e8660d] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
      >
        <Send className="h-4 w-4" />
        {isPending ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
}

export function HelpPage() {
  const [query, setQuery] = useState('');

  const filtered: FaqCategory[] = query.trim().length < 2
    ? FAQ_CATEGORIES
    : FAQ_CATEGORIES.map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) =>
            item.q.toLowerCase().includes(query.toLowerCase()) ||
            item.a.toLowerCase().includes(query.toLowerCase()),
        ),
      })).filter((cat) => cat.items.length > 0);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="font-heading text-3xl font-bold text-foreground">Help & Support</h1>
        <p className="mt-2 text-muted-foreground">Find answers to common questions or reach out to us.</p>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          placeholder="Search questions…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-xl border border-border bg-background py-3 pl-10 pr-4 text-sm outline-none transition-shadow focus:ring-2 focus:ring-[#e8660d]/20"
        />
      </div>

      {/* FAQ sections */}
      {filtered.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground">No results for &ldquo;{query}&rdquo;</p>
      ) : (
        <div className="space-y-6">
          {filtered.map((cat) => (
            <div key={cat.label} className="overflow-hidden rounded-xl border border-border bg-background">
              <div className="border-b border-border bg-muted/40 px-5 py-3">
                <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{cat.label}</h2>
              </div>
              <Accordion items={cat.items} />
            </div>
          ))}
        </div>
      )}

      {/* Contact card */}
      <div className="mt-10 rounded-2xl border border-border bg-background p-6 sm:p-8">
        <div className="mb-5 flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#e8660d]/10">
            <MessageCircle className="h-5 w-5 text-[#e8660d]" />
          </div>
          <div>
            <p className="font-semibold text-foreground">Still need help?</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Can&rsquo;t find what you&rsquo;re looking for? Send us a message and our support team
              typically responds within one business day.
            </p>
          </div>
        </div>
        <ContactForm />
      </div>
    </div>
  );
}
